import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Reveal } from "./Reveal";
import { useContent } from "./content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initials = (name) =>
  name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();

export const TeamSection = () => {
  const c = useContent();
  const [team, setTeam] = useState([]);

  useEffect(() => {
    axios.get(`${API}/team`).then((r) => setTeam(r.data)).catch(() => {});
  }, []);

  if (team.length === 0) return null;

  return (
    <section id="team" data-testid="team-section" className="py-24 md:py-32 bg-muted/60">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-2xl mb-16">
          <Reveal>
            <span className="uppercase tracking-[0.24em] text-xs font-bold text-accent">{c.team_eyebrow}</span>
            <h2 className="mt-4 font-display font-medium tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
              {c.team_title}
            </h2>
          </Reveal>
        </div>

        {team.length === 1 ? (
          <div data-testid="team-grid" className="w-full">
            {team.map((m) => (
              <Reveal key={m.id}>
                <motion.div
                  data-testid={`team-card-${m.id}`}
                  whileHover={{ y: -6 }}
                  transition={{ type: "spring", stiffness: 260, damping: 22 }}
                  className="group grid grid-cols-1 md:grid-cols-2 items-stretch bg-card border border-border rounded-3xl overflow-hidden hover:shadow-[0_30px_60px_-30px_rgba(18,119,110,0.3)] transition-shadow duration-500"
                >
                  <div className="aspect-[4/3] md:aspect-auto md:min-h-[340px] overflow-hidden bg-secondary/15 grid place-items-center">
                    {m.image ? (
                      <img src={m.image} alt={m.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    ) : (
                      <span className="font-heading text-6xl font-semibold text-primary/40">{initials(m.name)}</span>
                    )}
                  </div>
                  <div className="p-8 md:p-10 flex flex-col justify-center">
                    <h3 className="font-heading text-2xl md:text-3xl font-medium">{m.name}</h3>
                    <div className="text-sm text-accent font-medium mt-1">{m.role}</div>
                    {m.bio ? <p className="mt-4 text-foreground/65 leading-relaxed">{m.bio}</p> : null}
                  </div>
                </motion.div>
              </Reveal>
            ))}
          </div>
        ) : (
        <div
          data-testid="team-grid"
          className={`grid gap-6 md:gap-8 grid-cols-1 sm:grid-cols-2 ${
            team.length === 2 ? "lg:grid-cols-2 max-w-4xl mx-auto" : "lg:grid-cols-3"
          }`}
        >
          {team.map((m, i) => (
            <Reveal key={m.id} delay={i * 0.08}>
              <motion.div
                data-testid={`team-card-${m.id}`}
                whileHover={{ y: -8 }}
                transition={{ type: "spring", stiffness: 260, damping: 22 }}
                className="group h-full bg-card border border-border rounded-3xl overflow-hidden hover:shadow-[0_30px_60px_-30px_rgba(18,119,110,0.3)] transition-shadow duration-500"
              >
                <div className="aspect-[4/3] overflow-hidden bg-secondary/15 grid place-items-center">
                  {m.image ? (
                    <img src={m.image} alt={m.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  ) : (
                    <span className="font-heading text-5xl font-semibold text-primary/40">{initials(m.name)}</span>
                  )}
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-xl font-medium">{m.name}</h3>
                  <div className="text-sm text-accent font-medium mt-0.5">{m.role}</div>
                  {m.bio ? <p className="mt-3 text-sm text-foreground/65 leading-relaxed">{m.bio}</p> : null}
                </div>
              </motion.div>
            </Reveal>
          ))}
        </div>
        )}
      </div>
    </section>
  );
};
